import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, PackageCheck, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SimpleSelect } from "@/components/Filters";
import { useAuth } from "@/context/AuthContext";
import { useProducts } from "@/hooks/useData";
import { api, errMsg, isConflict, notifyStockChanged } from "@/lib/api";
import { todayStr } from "@/lib/format";

const WH_OPTS = [["A", "Warehouse A"], ["B", "Warehouse B"]];
const toInt = (v) => (/^\d+$/.test(String(v ?? "").trim()) ? Number(v) : NaN);

const calc = (it, r, canRevise, allowExcess) => {
  const prev = it.received || 0;
  const short = it.short_closed_qty || 0;
  const ordered = canRevise ? toInt(r.ordered) : it.qty;
  const lot = toInt(r.lot);
  const rej = toInt(r.rej);
  const pending = Number.isNaN(ordered) ? 0 : Math.max(ordered - prev - short, 0);
  const accepted = Number.isNaN(lot) || Number.isNaN(rej) ? 0 : lot - rej;
  const excess = accepted > pending;
  let err = "";
  if (Number.isNaN(ordered) || ordered < 1) err = "Ordered must be a whole number, 1 or more";
  else if (ordered < prev + short) err = `Ordered can't be lower than already received${short ? " + short closed" : ""} (${prev + short})`;
  else if (Number.isNaN(lot)) err = "Received qty must be a whole number, 0 or more";
  else if (Number.isNaN(rej)) err = "Rejected qty must be a whole number, 0 or more";
  else if (rej > lot) err = "Rejected can't be more than received in this lot";
  else if (excess && !allowExcess) err = `Accepted ${accepted} is more than pending ${pending}. ${canRevise ? "Raise the Ordered qty, or tick Allow excess below." : "Tick Allow excess below and give a reason."}`;
  return { prev, short, ordered, lot, rej, pending, accepted, excess, after: Math.max(pending - accepted, 0), err, changed: !Number.isNaN(ordered) && ordered !== it.qty };
};

const ProductCell = ({ it, idx, err, sfx = "" }) => (
  <>
    <div className="product-name font-medium" data-testid={`receive-product-name-${idx}${sfx}`}>{it.fullName}</div>
    {it.sku && <div className="mt-0.5 font-mono text-[11px] text-muted-foreground product-name" data-testid={`receive-product-sku-${idx}${sfx}`}>SKU: {it.sku}</div>}
    {err && <div className="mt-1 flex items-start gap-1 text-[11px] text-red-500 product-name" data-testid={`receive-error-${idx}${sfx}`}><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{err}</div>}
  </>
);

const QtyInput = ({ value, onChange, testid, warn }) => (
  <Input inputMode="numeric" value={value} onFocus={(e) => e.target.select()} onChange={onChange} className={`h-9 w-full max-w-[96px] font-mono ${warn ? "border-amber-500" : ""}`} data-testid={testid} />
);

const Cells = ({ it, idx, r, c, canRevise, set, sfx = "" }) => ({
  ordered: <>{canRevise ? <QtyInput value={r.ordered} onChange={(e) => set(it.product_id, "ordered", e.target.value)} testid={`receive-ordered-${idx}${sfx}`} warn={c.changed} />
    : <span className="font-mono" data-testid={`receive-ordered-${idx}${sfx}`}>{it.qty}</span>}{c.changed && <div className="text-[10px] text-amber-600">was {it.qty}</div>}</>,
  prev: <span className="font-mono text-muted-foreground" data-testid={`receive-prev-${idx}${sfx}`}>{c.prev}{c.short > 0 && <span className="block text-[10px]">short {c.short}</span>}</span>,
  pending: <span className="font-mono" data-testid={`receive-pending-before-${idx}${sfx}`}>{c.pending}</span>,
  lot: <QtyInput value={r.lot} onChange={(e) => set(it.product_id, "lot", e.target.value)} testid={`receive-qty-${idx}${sfx}`} />,
  rej: <QtyInput value={r.rej} onChange={(e) => set(it.product_id, "rej", e.target.value)} testid={`receive-rejected-${idx}${sfx}`} />,
  acc: <span className={`font-mono font-semibold ${c.excess ? "text-amber-600" : ""}`} data-testid={`receive-accepted-${idx}${sfx}`}>{c.accepted}</span>,
  after: <span className={`font-mono ${c.after > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid={`receive-pending-${idx}${sfx}`}>{c.after}</span>,
  remarks: <Input value={r.remarks} onChange={(e) => set(it.product_id, "remarks", e.target.value)} className="h-9" placeholder="—" data-testid={`receive-remarks-${idx}${sfx}`} />,
});

const COLS = [["ordered", "Ordered", "8%"], ["prev", "Received so far", "8%"], ["pending", "Pending", "7%"], ["lot", "This lot received", "9%"],
  ["rej", "Rejected", "8%"], ["acc", "Accepted", "7%"], ["after", "Pending after", "7%"], ["remarks", "Remarks", "13%"]];

const ReceiveTable = ({ list, rows, canRevise, set }) => (
  <div className="hidden md:block max-h-[50vh] overflow-y-auto rounded-md border border-border" data-testid="receive-table-wrap">
    <table className="data-table table-fixed" data-testid="receive-table">
      <colgroup><col className="w-[33%]" />{COLS.map(([k, , w]) => <col key={k} style={{ width: w }} />)}</colgroup>
      <thead className="[&_th]:!whitespace-normal"><tr><th>Product</th>{COLS.map(([k, l]) => <th key={k}>{l}</th>)}</tr></thead>
      <tbody>{list.map(({ it, c }, idx) => {
        const cells = Cells({ it, idx, r: rows[it.product_id] || {}, c, canRevise, set });
        return (
          <tr key={it.product_id} data-testid={`receive-row-${idx}`} className={`align-top ${c.err ? "bg-red-500/5" : ""}`}>
            <td className="wrap-cell text-xs"><ProductCell it={it} idx={idx} err={c.err} /></td>
            {COLS.map(([k]) => <td key={k} className={["prev", "pending", "acc", "after"].includes(k) ? "pt-4" : ""}>{cells[k]}</td>)}
          </tr>);
      })}</tbody>
    </table>
  </div>
);

const ReceiveCards = ({ list, rows, canRevise, set }) => (
  <div className="space-y-3 md:hidden" data-testid="receive-cards">
    {list.map(({ it, c }, idx) => {
      const cells = Cells({ it, idx, r: rows[it.product_id] || {}, c, canRevise, set, sfx: "-m" });
      return (
        <div key={it.product_id} className={`rounded-md border p-3 ${c.err ? "border-red-400 bg-red-500/5" : "border-border"}`} data-testid={`receive-card-${idx}`}>
          <div className="text-sm"><ProductCell it={it} idx={idx} err={c.err} sfx="-m" /></div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
            {COLS.map(([k, l]) => <div key={k} className={k === "remarks" ? "col-span-3" : ""}><div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>{cells[k]}</div>)}
          </div>
        </div>);
    })}
  </div>
);

const Summary = ({ po, list, wh, date, meta }) => {
  const changed = list.filter((x) => x.c.changed);
  const recv = list.filter((x) => x.c.lot > 0);
  return (
    <div className="space-y-3 text-sm" data-testid="receive-summary">
      {changed.length > 0 && <div><div className="mb-1 font-semibold">Ordered qty changes</div>
        {changed.map(({ it, c }) => <div key={it.product_id} className="flex items-start justify-between gap-3 py-0.5"><span className="product-name">{it.fullName}</span><span className="shrink-0 font-mono">{it.qty} → {c.ordered}</span></div>)}</div>}
      <div><div className="mb-1 font-semibold">Stock IN to Warehouse {wh} on {date}</div>
        {recv.map(({ it, c }) => (
          <div key={it.product_id} className="flex items-start justify-between gap-3 py-0.5"><span className="product-name">{it.fullName}{it.sku && <span className="ml-2 font-mono text-[11px] text-muted-foreground">{it.sku}</span>}</span>
            <span className="shrink-0 font-mono"><span className="text-emerald-600">+{c.accepted}</span>{c.rej > 0 && <span className="ml-2 text-red-500">rejected {c.rej}</span>}{c.excess && <span className="ml-2 text-amber-600">excess</span>}</span></div>))}
        <div className="mt-2 border-t border-border pt-2 font-mono text-xs" data-testid="receive-summary-total">Accepted units: {recv.reduce((s, x) => s + x.c.accepted, 0)} · Rejected: {recv.reduce((s, x) => s + x.c.rej, 0)} · Lines: {recv.length}</div></div>
      <p className="text-xs text-muted-foreground">{po.order_no} · {po.supplier || "No supplier"}{meta.challan_no && ` · Challan ${meta.challan_no}`}{meta.invoice_no && ` · Invoice ${meta.invoice_no}`}{meta.vehicle_no && ` · Vehicle ${meta.vehicle_no}`}</p>
      {meta.allow_excess && <p className="text-xs text-amber-600">Excess reason: {meta.excess_reason}</p>}
    </div>
  );
};

const Meta = ({ label, children }) => <div className="min-w-0 space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;

export const ReceiveDialog = ({ open, onOpenChange, order, onDone }) => {
  const { can } = useAuth();
  const canRevise = can("orders", "edit");
  const { products } = useProducts();
  const [date, setDate] = useState(todayStr());
  const [wh, setWh] = useState("A");
  const [meta, setMeta] = useState({});
  const [rows, setRows] = useState({});
  const [step, setStep] = useState("edit");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !order) return;
    setRows(Object.fromEntries(order.items.map((i) => [i.product_id, { ordered: String(i.qty), lot: "0", rej: "0", remarks: "" }])));
    setDate(todayStr()); setWh(order.warehouse || "A"); setStep("edit");
    setMeta({ challan_no: "", invoice_no: "", vehicle_no: "", note: "", allow_excess: false, excess_reason: "" });
  }, [open, order]);
  const set = (pid, k, v) => setRows((s) => ({ ...s, [pid]: { ...s[pid], [k]: v } }));
  const m = (k) => (e) => setMeta((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const list = useMemo(() => {
    const master = Object.fromEntries(products.map((p) => [p.id, p]));
    return (order?.items || []).map((item) => {
      const it = { ...item, fullName: master[item.product_id]?.name || item.name, sku: master[item.product_id]?.sku || "" };
      return { it, c: calc(it, rows[it.product_id] || { ordered: it.qty, lot: 0, rej: 0 }, canRevise, meta.allow_excess) };
    });
  }, [order, rows, canRevise, products, meta.allow_excess]);
  const anyExcess = list.some((x) => x.c.excess && x.c.lot > 0);
  const blocked = list.some((x) => x.c.err) || !list.some((x) => x.c.lot > 0) || date > todayStr() || (anyExcess && !meta.excess_reason?.trim());
  const fillPending = () => setRows((s) => Object.fromEntries(list.map(({ it, c }) => [it.product_id, { ...s[it.product_id], lot: String(c.pending), rej: "0" }])));
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const lines = list.map(({ it, c }) => ({ product_id: it.product_id, qty: c.lot, rejected: c.rej, remarks: rows[it.product_id]?.remarks || "", ordered: c.changed ? c.ordered : null }));
      const { data } = await api.post(`/purchase-orders/${order.id}/receive`, { date, warehouse: wh, expected_rev: order.rev || 0, ...meta, allow_excess: anyExcess && meta.allow_excess, lines });
      toast.success(`${data.receipt_no}: ${data.units} units received into WH ${wh} — order ${data.status === "closed" ? "closed (short)" : data.status}`);
      notifyStockChanged();
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
      if (isConflict(e)) { notifyStockChanged(); onOpenChange(false); }
    } finally { setBusy(false); }
  };
  if (!order) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1560px] sm:max-w-[1560px] max-h-[94vh] overflow-y-auto p-4 sm:p-6" data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5 text-primary" /> Record receipt · {order.order_no}</DialogTitle>
          <DialogDescription>Each delivery is saved as its own GRN. Type only what physically arrived in this lot.</DialogDescription>
        </DialogHeader>
        {step === "edit" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
              <Meta label="Supplier / vendor"><div className="product-name text-sm font-medium" data-testid="receive-supplier">{order.supplier || "—"}</div></Meta>
              <Meta label="Order"><div className="text-sm font-medium" data-testid="receive-order-no">{order.order_no} · {order.kind === "manufacturing" ? "MO" : "PO"}</div></Meta>
              <Meta label="Receipt date"><Input type="date" max={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="receive-date-input" /></Meta>
              <Meta label="Warehouse"><SimpleSelect value={wh} onChange={setWh} options={WH_OPTS} testid="receive-warehouse-select" className="h-9 w-full" /></Meta>
              <Meta label="Vendor challan no."><Input value={meta.challan_no || ""} onChange={m("challan_no")} className="h-9" data-testid="receive-challan-input" /></Meta>
              <Meta label="Vendor invoice no."><Input value={meta.invoice_no || ""} onChange={m("invoice_no")} className="h-9" data-testid="receive-invoice-input" /></Meta>
              <Meta label="Vehicle no."><Input value={meta.vehicle_no || ""} onChange={m("vehicle_no")} className="h-9" data-testid="receive-vehicle-input" /></Meta>
            </div>
            {date > todayStr() && <div className="text-xs text-red-500" data-testid="receive-date-error">Receipt date cannot be in the future</div>}
            <div className="flex justify-end"><Button type="button" size="sm" variant="outline" onClick={fillPending} data-testid="receive-fill-pending-btn"><Wand2 className="h-4 w-4" /> Fill all pending</Button></div>
            <ReceiveTable list={list} rows={rows} canRevise={canRevise} set={set} />
            <ReceiveCards list={list} rows={rows} canRevise={canRevise} set={set} />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2 rounded-md border border-border p-3">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!meta.allow_excess} onCheckedChange={(v) => setMeta((s) => ({ ...s, allow_excess: !!v }))} data-testid="receive-allow-excess" /> Allow excess (accept more than pending)</label>
                {meta.allow_excess && <Input value={meta.excess_reason || ""} onChange={m("excess_reason")} placeholder="Reason for excess, e.g. vendor sent 2 extra as replacement" data-testid="receive-excess-reason" />}
                {anyExcess && meta.allow_excess && !meta.excess_reason?.trim() && <div className="text-xs text-red-500" data-testid="receive-excess-error">A reason is required to accept excess</div>}
              </div>
              <Meta label="Notes"><Input value={meta.note || ""} onChange={m("note")} placeholder="Optional" data-testid="receive-note-input" /></Meta>
            </div>
          </div>
        ) : <Summary po={order} list={list} wh={wh} date={date} meta={{ ...meta, allow_excess: anyExcess && meta.allow_excess }} />}
        <DialogFooter className="gap-2">
          {step === "edit" ? <>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="receive-cancel-btn">Cancel</Button>
            <Button disabled={blocked} onClick={() => setStep("confirm")} data-testid="receive-confirm-btn">Save receipt (GRN)</Button>
          </> : <>
            <Button variant="ghost" onClick={() => setStep("edit")} disabled={busy} data-testid="receive-back-btn">Back</Button>
            <Button onClick={submit} disabled={busy} data-testid="receive-final-confirm-btn">{busy ? "Saving…" : "Yes, save GRN and add stock"}</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
